import { useDmNotificationPatient } from '../../../../hooks/useDmNotifications';
import type { DmNotificationFilters } from '../../../../services/dmNotifications';
import { CountTabLabel } from '../../../work-queue/tasks/CountTabLabel';
import { OpenNotificationsPanel } from './OpenNotificationsPanel';

// Only the patient block is read here; the neighbours these filters drive belong to the notifications list.
const PATIENT_ONLY_FILTERS: DmNotificationFilters = { sort: 'latest', order: 'desc' };

function usePatientOpenNotifications(patientId: string) {
    return useDmNotificationPatient(patientId, PATIENT_ONLY_FILTERS);
}

export function PatientNotificationsTabLabel({ patientId }: { patientId: string }) {
    const { data } = usePatientOpenNotifications(patientId);
    return <CountTabLabel label="Notifications" count={data ? data.patient.openCount : null} />;
}

function TabSkeleton() {
    return (
        <div className="flex flex-col gap-2 p-4" aria-busy="true">
            <div className="h-24 rounded-lg bg-gray-200 animate-pulse" />
            <div className="h-24 rounded-lg bg-gray-200 animate-pulse" />
        </div>
    );
}

function TabError({ onRetry }: { onRetry: () => void }) {
    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <p className="text-[13px] text-gray-600">Couldn't load this patient's notifications.</p>
            <button
                type="button"
                onClick={onRetry}
                className="rounded-lg bg-gray-900 px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-gray-800"
            >
                Retry
            </button>
        </div>
    );
}

interface PatientNotificationsTabProps {
    patientId: string;
    onNextPatient?: () => void;
    onBackToList?: () => void;
}

// A patient's open DM notifications inside a host's tab, worked exactly as on the notifications view.
export function PatientNotificationsTab({
    patientId,
    onNextPatient,
    onBackToList
}: PatientNotificationsTabProps) {
    const { data, isError, refetch } = usePatientOpenNotifications(patientId);
    if (data) {
        return (
            <OpenNotificationsPanel
                patient={data.patient}
                recentlyResolved={data.recentlyResolved}
                onNextPatient={onNextPatient}
                onBackToList={onBackToList}
                embedded
            />
        );
    }
    if (isError) return <TabError onRetry={() => void refetch()} />;
    return <TabSkeleton />;
}
